import {
  addElement,
  defaultGenericElement,
  deleteMultipleElements,
  reversionElement,
  updateElement,
} from '../../lib/element';
import { history } from '../../lib/history';
import { type KeyPayload, type PointerPayload } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { type GenericShape } from '../../lib/shapes';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { updateEditorStore } from '../../stores/useEditorStore';
import { pointDistance, type Point } from '../../utils/math';

import {
  appendHandleForPoint,
  initHandlesFromPoints,
  offsetHandles,
} from './utils/bezierHandles';
import { getPotentialIndexOfPoint } from './utils/bounds';
import { getHitPointIndex } from './utils/path';
import { deletePolyPoint, snapToPolyAngle } from './utils/point';
import { getPolyShape } from './utils/shape';
import { normalizePolyToBounds, offsetPoints } from './utils/transform';
import { translatePoint } from './utils/translation';

import {
  poly,
  type GenericPolyElement,
  type PolyElement,
  type PolyPlugin,
  type PointBezierHandle,
} from '.';

const MIN_DISTANCE_TO_CLOSE_POLY = 20;
const MIN_DISTANCE_TO_START_DRAGGING = 20;
const MIN_DISTANCE_BETWEEN_POINTS = 5;
const MIN_DISTANCE_TO_ADD_POINT = 20;
const MIN_POINTS = 3;

export const events: PolyPlugin['events'] = {
  onPointerDown: (payload) => {
    const { event, helpers, runtimeState, elementState } = payload;
    const { button, pos } = event;

    // start creating a new poly
    if (
      button === 'left' &&
      runtimeState.activeTool === 'poly' &&
      !runtimeState.newElement
    ) {
      const newElement: PolyElement = {
        ...defaultGenericElement(),
        originX: pos.x,
        originY: pos.y,
        points: [[0, 0]],
        isOpen: true,
        path: new Path2D(),
        strokeColor: poly.state.newStrokeColor,
        strokeWidth: poly.state.newStrokeWidth,
        type: 'poly',
        booleanMode: 'intersect',
        x: pos.x,
        y: pos.y,
      };

      helpers.updateRuntimeStore({
        newElement: newElement,
        originalNewElement: {
          ...newElement,
        },
      });
      helpers.showNewElementCanvas();
      helpers.renderNewElementScene();
      return;
    }

    // add a new point to a pending poly
    if (
      button === 'left' &&
      runtimeState.activeTool === 'poly' &&
      runtimeState.originalNewElement
    ) {
      const originalElement = runtimeState.originalNewElement as PolyElement;

      const distanceToFirstPoint = pointDistance(originalElement.points[0], [
        pos.x - originalElement.x,
        pos.y - originalElement.y,
      ]);

      // closing a pending poly
      if (
        originalElement.points.length > 1 &&
        distanceToFirstPoint <
          MIN_DISTANCE_TO_CLOSE_POLY / runtimeState.zoom.value
      ) {
        const normalized = normalizePolyToBounds(
          originalElement.points,
          originalElement.bezierHandles,
          originalElement.x,
          originalElement.y,
        );
        const closedElement: PolyElement = {
          ...originalElement,
          ...normalized,
          isOpen: false,
        };
        closedElement.shape = getPolyShape(closedElement);
        addElement(elementState.elements, closedElement);

        history.push(
          updateEditorStore({ selectedElementIds: [closedElement.id] }),
        );
        updateRuntimeStore({
          activeTool: 'selection',
          newElement: undefined,
          originalNewElement: undefined,
        });

        helpers.renderStaticScene();
        helpers.hideNewElementCanvas();
      } else {
        // add a new point to the pending poly
        let newPoint: Point = [
          pos.x - originalElement.x,
          pos.y - originalElement.y,
        ];

        poly.actions.setDraggingStartPoint([pos.x, pos.y]);

        // snap the new point to an angle if shift is pressed
        if (
          runtimeState.activeKeyPress?.shift &&
          originalElement.points.length > 0
        ) {
          const lastPoint = originalElement.points.at(-1)!;
          const lastPointAbsX = lastPoint[0] + originalElement.x;
          const lastPointAbsY = lastPoint[1] + originalElement.y;

          const snappedPos = snapToPolyAngle(
            pos,
            lastPointAbsX,
            lastPointAbsY,
            originalElement.points,
          );

          newPoint = [
            snappedPos.x - originalElement.x,
            snappedPos.y - originalElement.y,
          ];
        }

        const newPoints = [...originalElement.points, newPoint];
        const bezierHandles = appendHandleForPoint(
          originalElement.bezierHandles,
          newPoint,
        );

        const normalized = normalizePolyToBounds(
          newPoints,
          bezierHandles,
          originalElement.x,
          originalElement.y,
        );

        const newElement: PolyElement = {
          ...originalElement,
          ...normalized,
        };

        newElement.shape = getPolyShape(newElement);
        newElement.version = {
          ...newElement.version,
          ...reversionElement(newElement, ['bounds', 'canvas', 'path']).version,
        };
        helpers.updateRuntimeStore({ originalNewElement: newElement });
        helpers.renderNewElementScene();
        helpers.renderStaticScene();
      }

      return;
    }

    if (elementState.selectedElementIds.length !== 1) return;

    const activeElement = elementState.elements.get(
      elementState.selectedElementIds[0],
    ) as GenericPolyElement | undefined;

    if (!activeElement || activeElement.type !== 'poly') return;

    handleAdditionOfSinglePoint(payload, activeElement);
    handleRemovalOfSinglePoint(payload, activeElement);
  },
  onPointerMove: ({ event, helpers, runtimeState }) => {
    const { pos, button } = event;

    // preview a pending poly
    if (
      runtimeState.newElement &&
      runtimeState.newElement.type === 'poly' &&
      button !== 'middle'
    ) {
      const element = runtimeState.newElement as PolyElement;
      const originalElement = runtimeState.originalNewElement as PolyElement;

      let currentPos = pos;

      // snap the current position to an angle if shift is pressed
      if (
        runtimeState.activeKeyPress?.shift &&
        originalElement.points.length > 0
      ) {
        const lastPoint = originalElement.points.at(-1)!;
        const lastPointAbsX = lastPoint[0] + originalElement.x;
        const lastPointAbsY = lastPoint[1] + originalElement.y;

        currentPos = snapToPolyAngle(
          pos,
          lastPointAbsX,
          lastPointAbsY,
          originalElement.points,
        );
      }

      // trigger bezier handle mode if threshold is reached
      if (
        !poly.state.isDragging &&
        poly.state.draggingStartPoint &&
        pointDistance([pos.x, pos.y], poly.state.draggingStartPoint) >
          MIN_DISTANCE_TO_START_DRAGGING / runtimeState.zoom.value
      ) {
        poly.actions.setIsDragging(true);
      } else if (
        poly.state.isDragging &&
        poly.state.draggingStartPoint &&
        pointDistance([pos.x, pos.y], poly.state.draggingStartPoint) <=
          MIN_DISTANCE_TO_START_DRAGGING / runtimeState.zoom.value
      ) {
        poly.actions.setIsDragging(false);
      }

      let newElement = element;

      // add bezier handles to the pending poly
      if (poly.state.isDragging && poly.state.draggingStartPoint) {
        const dx = currentPos.x - poly.state.draggingStartPoint[0];
        const dy = currentPos.y - poly.state.draggingStartPoint[1];

        const newPoints = originalElement.points.slice();
        const lastPoint = newPoints.at(-1)!;

        const baseHandles = originalElement.bezierHandles
          ? originalElement.bezierHandles.slice(0, -1)
          : initHandlesFromPoints(originalElement.points.slice(0, -1));

        const bezierHandles: PointBezierHandle[] = [
          ...baseHandles,
          {
            after: [lastPoint[0] + dx, lastPoint[1] + dy],
            before: [lastPoint[0] - dx, lastPoint[1] - dy],
          },
        ];

        newElement = {
          ...newElement,
          ...normalizePolyToBounds(
            newPoints,
            bezierHandles,
            originalElement.x,
            originalElement.y,
          ),
        };
      } else {
        // add a new point to the pending poly
        const originPosition = [originalElement.x, originalElement.y] as Point;

        const newPoints: Point[] = [
          ...originalElement.points.slice(),
          [currentPos.x - originPosition[0], currentPos.y - originPosition[1]],
        ];

        const bezierHandles = appendHandleForPoint(
          originalElement.bezierHandles,
          newPoints[newPoints.length - 1],
        );

        newElement = {
          ...newElement,
          ...normalizePolyToBounds(
            newPoints,
            bezierHandles,
            originalElement.x,
            originalElement.y,
          ),
        };
      }

      newElement.shape = getPolyShape(newElement);
      newElement.version = {
        ...newElement.version,
        ...reversionElement(element, ['bounds', 'canvas', 'path']).version,
      };

      helpers.updateRuntimeStore({ newElement });
      helpers.renderNewElementScene();
    }
  },
  onPointerUp: ({ runtimeState, helpers }) => {
    // add bezier handles to the pending poly
    if (poly.state.isDragging && runtimeState.newElement) {
      const newElement = runtimeState.newElement as GenericPolyElement;

      if (newElement?.bezierHandles) {
        const originalElement = runtimeState.originalNewElement as PolyElement;

        const updatedOriginalElement = {
          ...originalElement,
          x: newElement.x,
          y: newElement.y,
          width: newElement.width,
          height: newElement.height,
          points: newElement.points,
          bezierHandles: newElement.bezierHandles,
        };

        helpers.updateRuntimeStore({
          originalNewElement: updatedOriginalElement,
        });

        helpers.renderNewElementScene();
        helpers.renderStaticScene();
      }
    }

    poly.actions.setIsDragging(false);
    poly.actions.setDraggingStartPoint(null);
  },
  onKeyDown: (payload) => {
    const { event, runtimeState, elementState, helpers } = payload;
    handlePolyPointShortcuts(payload, 'poly');
    handleDeletePolyPointShortcut(payload, 'poly');

    if (event.key === 'Backspace' && runtimeState.newElement) {
      const currentElement = runtimeState.newElement as PolyElement;
      const originalElement = runtimeState.originalNewElement as PolyElement;

      if (originalElement.points.length <= 1) {
        deleteMultipleElements(elementState.elements, [currentElement.id]);
        updateRuntimeStore({
          newElement: undefined,
          originalNewElement: undefined,
        });
        helpers.hideNewElementCanvas();
        helpers.renderStaticScene();
        return;
      }

      const trimmedPoints = originalElement.points.slice(0, -1);
      const trimmedHandles = originalElement.bezierHandles?.slice(0, -1);

      const normalized = normalizePolyToBounds(
        offsetPoints(trimmedPoints, originalElement.x, originalElement.y),
        offsetHandles(trimmedHandles, originalElement.x, originalElement.y),
        0,
        0,
      );

      const newOriginalElement: PolyElement = {
        ...originalElement,
        ...normalized,
      };

      const newCurrentElement: PolyElement = {
        ...newOriginalElement,
        id: currentElement.id,
      };

      newCurrentElement.shape = getPolyShape(newCurrentElement);
      newCurrentElement.version = {
        ...currentElement.version,
        ...reversionElement(newCurrentElement, ['bounds', 'canvas', 'path'])
          .version,
      };

      helpers.updateRuntimeStore({
        newElement: newCurrentElement,
        originalNewElement: newOriginalElement,
      });

      helpers.renderNewElementScene();
      helpers.renderStaticScene();
    }

    if (event.key === 'Escape' && runtimeState.newElement) {
      const element = runtimeState.originalNewElement as PolyElement;

      if (element.points.length > 2) {
        const normalized = normalizePolyToBounds(
          element.points,
          element.bezierHandles,
          element.x,
          element.y,
        );
        const closedElement: PolyElement = {
          ...element,
          ...normalized,
          isOpen: false,
        };
        closedElement.shape = getPolyShape(closedElement);
        addElement(elementState.elements, closedElement);

        history.push(
          updateEditorStore({ selectedElementIds: [closedElement.id] }),
        );
        updateRuntimeStore({
          activeTool: 'selection',
          newElement: undefined,
          originalNewElement: undefined,
        });
      } else {
        deleteMultipleElements(elementState.elements, [
          runtimeState.newElement.id,
        ]);
        updateRuntimeStore({
          newElement: undefined,
          originalNewElement: undefined,
        });
      }

      helpers.renderStaticScene();
      helpers.renderInteractiveScene();
      helpers.hideNewElementCanvas();
    }
  },
  onKeyUp: (payload) => {
    restoreLastActiveToolSubMode(payload, 'poly');
  },
  blockTouchPanning: true,
};

export const handlePolyPointShortcuts = <T extends GenericPolyElement>(
  { event, runtimeState, elementState, helpers }: KeyPayload,
  toolName: string,
) => {
  if (
    runtimeState.activeTool !== 'selection' ||
    elementState.selectedElementIds.length !== 1 ||
    runtimeState.newElement
  ) {
    return;
  }

  const activeElement = elementState.elements.get(
    elementState.selectedElementIds[0],
  ) as T | undefined;

  if (
    !activeElement ||
    activeElement.type !== toolName ||
    !elementState.polygonPointsVisible
  ) {
    return;
  }

  if (event.ctrlKey && runtimeState.activeToolSubMode !== 'poly_add_point') {
    helpers.updateRuntimeStore({
      lastActiveToolSubMode: runtimeState.activeToolSubMode,
      activeToolSubMode: 'poly_add_point',
    });
    return;
  }

  if (event.altKey && runtimeState.activeToolSubMode !== 'poly_delete_point') {
    event.preventDefault();
    helpers.updateRuntimeStore({
      lastActiveToolSubMode: runtimeState.activeToolSubMode,
      activeToolSubMode: 'poly_delete_point',
    });
    return;
  }
};

export const handleDeletePolyPointShortcut = <T extends GenericPolyElement>(
  { event, runtimeState, elementState, helpers }: KeyPayload,
  toolName: string,
) => {
  if (
    event.key !== 'Delete' ||
    elementState.selectedSubElementIds.length === 0 ||
    elementState.selectedElementIds.length !== 1
  ) {
    return;
  }

  const selectedElementId = elementState.selectedElementIds[0];
  const activeElement = elementState.elements.get(selectedElementId) as
    | T
    | undefined;

  if (!activeElement || activeElement.type !== toolName) return;

  const pointIndices = elementState.selectedSubElementIds
    .map((id) => Number.parseInt(id, 10))
    .filter(
      (index) =>
        !Number.isNaN(index) &&
        index >= 0 &&
        index < activeElement.points.length,
    )
    .sort((a, b) => b - a);

  if (pointIndices.length === 0) return;

  event.stopImmediatePropagation();

  if (activeElement.points.length - pointIndices.length < MIN_POINTS) {
    runtimeState.onEditorNotification?.({
      type: 'warning',
      reason: 'pathMinPoints',
    });
    return;
  }

  let newElement = activeElement;
  for (const index of pointIndices) {
    newElement = deletePolyPoint(newElement, index);
  }

  updateElement(elementState.elements, activeElement, newElement);

  history.push(
    updateEditorStore({
      selectedSubElementIds: [],
    }),
  );

  helpers.renderInteractiveScene();
  helpers.renderStaticScene();
};

export const restoreLastActiveToolSubMode = (
  { event, runtimeState, elementState, helpers }: KeyPayload,
  toolName: string,
) => {
  if (
    !(event.key === 'Control' || event.key === 'Alt') ||
    runtimeState.activeTool !== 'selection' ||
    elementState.selectedElementIds.length !== 1 ||
    runtimeState.lastActiveToolSubMode === undefined ||
    (runtimeState.activeToolSubMode !== 'poly_add_point' &&
      runtimeState.activeToolSubMode !== 'poly_delete_point')
  ) {
    return;
  }

  const activeElement = elementState.elements.get(
    elementState.selectedElementIds[0],
  );

  if (!activeElement || activeElement.type !== toolName) return;

  helpers.updateRuntimeStore({
    activeToolSubMode: runtimeState.lastActiveToolSubMode,
    lastActiveToolSubMode: undefined,
  });
  helpers.renderInteractiveScene();
};

export const handleRemovalOfSinglePoint = <T extends GenericPolyElement>(
  payload: PointerPayload,
  activeElement: T,
) => {
  const { event, elementState, runtimeState } = payload;
  const { button, pos } = event;

  const normalizedX = pos.x - activeElement.x;
  const normalizedY = pos.y - activeElement.y;

  const hitPointIndex = getHitPointIndex(
    activeElement.points,
    [normalizedX, normalizedY],
    MIN_DISTANCE_BETWEEN_POINTS,
  );

  if (
    button !== 'left' ||
    hitPointIndex === -1 ||
    runtimeState.activeToolSubMode !== 'poly_delete_point'
  ) {
    return;
  }

  if (activeElement.points.length <= MIN_POINTS) {
    runtimeState.onEditorNotification?.({
      type: 'warning',
      reason: 'pathMinPoints',
    });
    return;
  }

  const nextPoint =
    activeElement.points[(hitPointIndex + 1) % activeElement.points.length];
  const currentPoint = activeElement.points[hitPointIndex];
  const updated = translatePoint(
    hitPointIndex,
    activeElement,
    activeElement,
    nextPoint,
    currentPoint,
  );
  activeElement = { ...activeElement, ...updated };
  const newElement = deletePolyPoint(activeElement, hitPointIndex);

  updateElement(elementState.elements, activeElement, newElement);

  history.push(
    updateEditorStore({
      selectedSubElementIds: [],
    }),
  );

  renderStaticScene();
};

export const handleAdditionOfSinglePoint = <T extends GenericPolyElement>(
  payload: PointerPayload,
  activeElement: T,
  getNewElementShape: (element: T) => GenericShape = getPolyShape,
) => {
  const { event, elementState, runtimeState } = payload;
  const { button, pos } = event;

  const normalizedX = pos.x - activeElement.x;
  const normalizedY = pos.y - activeElement.y;

  const hitPointIndex = getHitPointIndex(
    activeElement.points,
    [normalizedX, normalizedY],
    MIN_DISTANCE_BETWEEN_POINTS,
  );

  if (
    button !== 'left' ||
    hitPointIndex !== -1 ||
    runtimeState.newElement ||
    runtimeState.activeToolSubMode !== 'poly_add_point'
  ) {
    return;
  }

  const newIndex = getPotentialIndexOfPoint(
    activeElement.points,
    [normalizedX, normalizedY],
    activeElement.bezierHandles,
    MIN_DISTANCE_TO_ADD_POINT,
  );

  if (newIndex === -1) return;

  // Check minimum distance to prevent duplicate points
  const newPoint: Point = [normalizedX, normalizedY];
  const prevPointIndex =
    newIndex === 0 ? activeElement.points.length - 1 : newIndex - 1;
  const prevPoint = activeElement.points[prevPointIndex];
  const nextPoint =
    activeElement.points[newIndex % activeElement.points.length];

  const distanceToPrev = pointDistance(newPoint, prevPoint);
  const distanceToNext = pointDistance(newPoint, nextPoint);

  const minDistanceThreshold =
    MIN_DISTANCE_BETWEEN_POINTS / runtimeState.zoom.value;

  if (Math.min(distanceToPrev, distanceToNext) < minDistanceThreshold) {
    return;
  }

  const newPoints = [...activeElement.points];
  newPoints.splice(newIndex, 0, newPoint);
  const newBezierHandles = activeElement.bezierHandles
    ? [...activeElement.bezierHandles]
    : undefined;
  if (newBezierHandles) {
    newBezierHandles.splice(newIndex, 0, {
      before: [normalizedX, normalizedY],
      after: [normalizedX, normalizedY],
    });
  }

  const newElement = {
    ...activeElement,
    points: newPoints,
    bezierHandles: newBezierHandles,
  } as T;

  newElement.shape = getNewElementShape(newElement);

  newElement.version = {
    ...newElement.version,
    ...reversionElement(activeElement, ['bounds', 'canvas', 'path']).version,
  };

  updateElement(elementState.elements, activeElement, newElement);

  history.push(
    updateEditorStore({
      selectedSubElementIds: [],
    }),
  );
  renderStaticScene();
};
